import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';

import AccountEdit from './AccountEdit';
import AccountService from '../../services/account.service';
import { a11yProps } from '../../helpers/utils';
import completeProfiles, { isoToFlagEmoji, subscriptionStatusColor } from './AccountUtils';
import dayjs from 'dayjs';
import Products from '../products/Products';
import Transactions from './Transactions';
import Payouts from './Payouts';
import TrialModal from './TrialModal';
import MarkAsPaidModal from './MarkAsPaidModal';
import BlockAccountModal from './BlockAccountModal';
import LiftSalesGateModal from './LiftSalesGateModal';

import TabPanel from '../subcomponents/TabPanel';
import Modal from '../subcomponents/Modal';

import { deepOrange } from '@mui/material/colors';
import {
  Create as CreateIcon,
  Tune as TuneIcon,
  ContentCopy as ContentCopyIcon,
  InfoOutlined as InfoOutlinedIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { CircularProgress, Snackbar, Alert, Button, Avatar, Paper, Grid, Box, Tab, Tabs, Typography, Link, Chip, IconButton, Tooltip } from '@mui/material';

import DOMPurify from 'dompurify';

const TAB_HASHES = ['products', 'transactions', 'payouts'];

const getTabFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  const index = TAB_HASHES.indexOf(hash);
  return index >= 0 ? index : 0;
};

const SALES_GATE_TOOLTIP = (
  <Box sx={{ p: 0.5, maxWidth: 320 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
      The sales gate is applied automatically when a trial account hits its free-sales limit, or when the
      account&apos;s Creala subscription is canceled. While it is on:
    </Typography>
    <Box component="ul" sx={{ pl: 2, m: 0, mb: 1 }}>
      <li><Typography variant="caption">Deactivate every product, price, and payment link — in the database and disabled in Stripe — so the account can&apos;t take new sales.</Typography></li>
      <li><Typography variant="caption">Keep products created while gated off Stripe entirely.</Typography></li>
      <li><Typography variant="caption">Email the user and notify the team in Slack.</Typography></li>
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
      It will NOT:
    </Typography>
    <Box component="ul" sx={{ pl: 2, m: 0, mb: 1 }}>
      <li><Typography variant="caption">Block the user from logging in or disable their websites.</Typography></li>
      <li><Typography variant="caption">Cancel subscriptions or touch Stripe payouts / the Connect account.</Typography></li>
    </Box>
    <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
      Lifting the gate re-activates every gated product, price, and payment link (in the database and Stripe)
      and rebuilds the Stripe objects for anything created while gated.
    </Typography>
  </Box>
);

const AccountProfile = () => {
  let params = useParams();
  let accountId = params.accountId;

  const [tabValue, setTabValue] = useState(getTabFromHash);
  const [infoTab, setInfoTab] = useState(0);
  const [account, setAccount] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const [openMarkAsPaidModal, setOpenMarkAsPaidModal] = useState(false);
  const [openBlockModal, setOpenBlockModal] = useState(false);
  const [openLiftSalesGateModal, setOpenLiftSalesGateModal] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getAccount(accountId);
  }, [accountId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    window.history.replaceState(null, '', `#${TAB_HASHES[newValue]}`);
  };

  const handleInfoTabChange = (event, newValue) => {
    setInfoTab(newValue);
  };

  async function loginToAccount() {
    try {
      const response = await AccountService.loginIntoAccount(account.id, account.email);
      if (response.data.success) {
        window.open(process.env.REACT_APP_WEBAPP_URL + 'login?ut=' + response.data.token, '_blank');
      }
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    const onHashChange = () => setTabValue(getTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  function goToEditAccount() {
    // This should open an modal instead of a new page
    setOpenModal(true);
  }

  const handleCloseModal = (event, wasSaved = false) => {
    if (wasSaved) {
      setSaved(true);
      getAccount(accountId);
    }
    setOpenModal(false);
    setTimeout(() => {
      setSaved(false);
    }, '4000');
  };

  const handleOpenTrialModal = () => {
    setOpenTrialModal(true);
  };

  const handleCloseTrialModal = () => {
    setOpenTrialModal(false);
  };

  const handleTrialSuccess = () => {
    setSaved(true);
    getAccount(accountId);
    setTimeout(() => {
      setSaved(false);
    }, 4000);
  };

  const handleOpenMarkAsPaidModal = () => {
    setOpenMarkAsPaidModal(true);
  };

  const handleCloseMarkAsPaidModal = () => {
    setOpenMarkAsPaidModal(false);
  };

  const handleMarkAsPaidSuccess = () => {
    setSaved(true);
    getAccount(accountId);
    setTimeout(() => {
      setSaved(false);
    }, 4000);
  };

  const handleOpenBlockModal = () => {
    setOpenBlockModal(true);
  };

  const handleCloseBlockModal = () => {
    setOpenBlockModal(false);
  };

  const handleBlockSuccess = () => {
    setSaved(true);
    getAccount(accountId);
    setTimeout(() => {
      setSaved(false);
    }, 4000);
  };

  const handleOpenLiftSalesGateModal = () => {
    setOpenLiftSalesGateModal(true);
  };

  const handleCloseLiftSalesGateModal = () => {
    setOpenLiftSalesGateModal(false);
  };

  const handleLiftSalesGateSuccess = () => {
    setSaved(true);
    getAccount(accountId);
    setTimeout(() => {
      setSaved(false);
    }, 4000);
  };

  const getAccount = (id) => {
    AccountService.get(id)
      .then((response) => {
        let parsedData = response.data;

        parsedData['completedProfile'] = completeProfiles([response.data])[0].completedProfile;

        parsedData['receiveNewsByEmail'] = response.data.receiveNewsByEmail === '1' ? true : false;
        parsedData['bio'] = response.data.bio;
        setAccount(parsedData);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={3}>
        <Paper elevation={3} sx={{ width: '100%', p: 3 }}>
          {account?.id !== undefined ? (
            <>
              {/* Avatar */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                {account?.profilePicture ? (
                  <Avatar sx={{ width: 100, height: 100 }} src={account?.profilePicture} />
                ) : (
                  <Avatar sx={{ bgcolor: deepOrange[500], width: 100, height: 100, fontSize: 14 }}>No pic</Avatar>
                )}
                <Typography variant="h6" sx={{ mt: 1, fontWeight: 600, textAlign: 'center' }}>
                  {account?.firstName} {account?.lastName}{' '}
                  <Box component="span" sx={{ fontSize: '1.1rem' }} aria-hidden>
                    {isoToFlagEmoji(account?.country || account?.signupIpCountry)}
                  </Box>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    #{account?.id} · {account?.email}
                  </Typography>
                  <Tooltip title="Copy email">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(account?.email)} sx={{ p: 0.25 }}>
                      <ContentCopyIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Primary actions */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button size="small" variant="outlined" startIcon={<CreateIcon />} onClick={goToEditAccount} fullWidth>
                  Edit
                </Button>
                <Button size="small" variant="outlined" startIcon={<TuneIcon />} component={RouterLink} to={`/accounts/${accountId}/account-plan`} fullWidth>
                  Plan
                </Button>
              </Box>
              <Button size="small" variant="contained" startIcon={<VpnKeyIcon />} onClick={loginToAccount} fullWidth sx={{ mb: 2 }}>
                Login into account
              </Button>

              {/* Critical status — always visible when active */}
              {account?.isBlocked && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <b>Account blocked.</b>
                  {account?.blockedReason ? <> Reason: {account.blockedReason}</> : null}
                </Alert>
              )}
              {account?.salesGated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <b>Sales gate applied.</b>
                  {account?.salesGatedReason ? <> Reason: {account.salesGatedReason}.</> : null}
                  {account?.salesGatedAt ? <> Applied {new Date(account.salesGatedAt).toLocaleDateString()}.</> : null}
                  <Tooltip arrow placement="right" title={SALES_GATE_TOOLTIP}>
                    <IconButton size="small" color="inherit" aria-label="What is the sales gate?" sx={{ ml: 0.5, p: 0.25 }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Alert>
              )}

              {/* Tabbed detail sections */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={infoTab} onChange={handleInfoTabChange} variant="fullWidth" aria-label="Account details">
                  <Tab label="Details" id="info-tab-0" aria-controls="info-tabpanel-0" sx={{ minWidth: 0, px: 1, fontSize: '0.72rem' }} />
                  <Tab label="Stripe" id="info-tab-1" aria-controls="info-tabpanel-1" sx={{ minWidth: 0, px: 1, fontSize: '0.72rem' }} />
                  <Tab label="Moderation" id="info-tab-2" aria-controls="info-tabpanel-2" sx={{ minWidth: 0, px: 1, fontSize: '0.72rem' }} />
                </Tabs>
              </Box>

              {/* Details tab */}
              {infoTab === 0 && (
                <Box role="tabpanel" id="info-tabpanel-0" aria-labelledby="info-tab-0" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
                      Plan
                    </Typography>
                    {account?.accountPlan?.subscriptionStatus === 'none' ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip
                          label={`Trial · ${Math.max(0, Math.ceil(account?.accountPlan?.trialDuration - (new Date() - new Date(account?.accountPlan?.createdAt)) / (1000 * 60 * 60 * 24)))} days left`}
                          size="small"
                          color="warning"
                          sx={{ alignSelf: 'flex-start' }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Button size="small" variant="outlined" onClick={handleOpenTrialModal} fullWidth>
                            Update Trial
                          </Button>
                          <Button size="small" variant="contained" color="warning" onClick={handleOpenMarkAsPaidModal} fullWidth>
                            Mark as Paid
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip
                          label={`${account?.accountPlan?.plan?.name} · $${account?.accountPlan?.plan?.price}`}
                          size="small"
                          color={subscriptionStatusColor(account?.accountPlan?.subscriptionStatus)}
                          sx={{ alignSelf: 'flex-start' }}
                        />
                        {account?.accountPlan?.subscriptionStatus === 'canceled' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                                Cancellation Requested
                              </Typography>
                              <Typography variant="body2">
                                {account?.accountPlan?.cancellationRequestDate
                                  ? dayjs(account.accountPlan.cancellationRequestDate).format('MMM D, YYYY')
                                  : '—'}
                              </Typography>
                            </Box>
                            {(account?.accountPlan?.reason || account?.accountPlan?.cancellationReason) && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                                  Reason
                                </Typography>
                                <Typography variant="body2">
                                  {account.accountPlan.reason ?? account.accountPlan.cancellationReason}
                                </Typography>
                                {account.accountPlan.reason && account.accountPlan.cancellationReason && (
                                  <Typography variant="caption" color="text.secondary">
                                    Stripe: {account.accountPlan.cancellationReason}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>

                  {account?.bio && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        Bio
                      </Typography>
                      <Typography variant="body2" component="div"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(account?.bio) }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Stripe tab */}
              {infoTab === 1 && (
                <Box role="tabpanel" id="info-tabpanel-1" aria-labelledby="info-tab-1" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Stripe Connect
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {account?.stripeAccountId ? (
                        <Link
                          href={`https://dashboard.stripe.com/acct_1L6gU3B0ixNROaBv/connect/accounts/${account.stripeAccountId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {account.stripeAccountId}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Stripe Subscription
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {account?.accountPlan?.stripeSubscriptionId ? (
                        <Link
                          href={`https://dashboard.stripe.com/acct_1L6gU3B0ixNROaBv/subscriptions/${account.accountPlan.stripeSubscriptionId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {account.accountPlan.stripeSubscriptionId}
                        </Link>
                      ) : account?.accountPlan?.stripeCustomerId ? (
                        <Link
                          href={`https://dashboard.stripe.com/acct_1L6gU3B0ixNROaBv/customers/${account.accountPlan.stripeCustomerId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {account.accountPlan.stripeCustomerId}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Moderation tab */}
              {infoTab === 2 && (
                <Box role="tabpanel" id="info-tabpanel-2" aria-labelledby="info-tab-2" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant={account?.isBlocked ? 'outlined' : 'contained'}
                      color="error"
                      onClick={handleOpenBlockModal}
                      sx={{ flex: 1 }}
                    >
                      {account?.isBlocked ? 'Unblock account' : 'Block account (fraud)'}
                    </Button>
                    <Tooltip
                      arrow
                      placement="right"
                      title={
                        <Box sx={{ p: 0.5, maxWidth: 320 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Blocking the account will:
                          </Typography>
                          <Box component="ul" sx={{ pl: 2, m: 0, mb: 1 }}>
                            <li><Typography variant="caption">Prevent the user from logging in (flags the account as blocked and records the reason).</Typography></li>
                            <li><Typography variant="caption">Archive every active product — including its Stripe prices, payment links, and product entries.</Typography></li>
                            <li><Typography variant="caption">Disable every website owned by this account.</Typography></li>
                            <li><Typography variant="caption">Notify the team in Slack.</Typography></li>
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            It will NOT:
                          </Typography>
                          <Box component="ul" sx={{ pl: 2, m: 0, mb: 1 }}>
                            <li><Typography variant="caption">Block Stripe payouts or touch the Stripe Connect account.</Typography></li>
                            <li><Typography variant="caption">Cancel active subscriptions.</Typography></li>
                            <li><Typography variant="caption">Revoke existing sessions or email the user.</Typography></li>
                          </Box>
                          <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                            Unblocking restores login, re-enables websites, and brings back the products the block archived. If Stripe refuses to re-enable one, it stays archived and the response says which.
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton size="small" aria-label="What does blocking do?">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {account?.salesGated && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={handleOpenLiftSalesGateModal}
                        sx={{ flex: 1 }}
                      >
                        Lift sales gate
                      </Button>
                      <Tooltip arrow placement="right" title={SALES_GATE_TOOLTIP}>
                        <IconButton size="small" aria-label="What is the sales gate?">
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={9}>
        <Snackbar open={saved} autoHideDuration={6000}>
          <Alert severity="success" variant="filled" sx={{ mb: 1 }}>
            The account was saved!
          </Alert>
        </Snackbar>
        <Modal open={openModal} title={'Edit account'}>
          <AccountEdit handleCloseModal={handleCloseModal} />
        </Modal>
        <TrialModal
          open={openTrialModal}
          onClose={handleCloseTrialModal}
          accountId={accountId}
          trialDuration={account?.accountPlan?.trialDuration}
          trialStartDate={account?.accountPlan?.createdAt}
          onSuccess={handleTrialSuccess}
        />
        <MarkAsPaidModal
          open={openMarkAsPaidModal}
          onClose={handleCloseMarkAsPaidModal}
          accountId={accountId}
          onSuccess={handleMarkAsPaidSuccess}
        />
        <BlockAccountModal
          open={openBlockModal}
          onClose={handleCloseBlockModal}
          accountId={accountId}
          isBlocked={!!account?.isBlocked}
          onSuccess={handleBlockSuccess}
        />
        <LiftSalesGateModal
          open={openLiftSalesGateModal}
          onClose={handleCloseLiftSalesGateModal}
          accountId={accountId}
          reason={account?.salesGatedReason}
          gatedAt={account?.salesGatedAt}
          onSuccess={handleLiftSalesGateSuccess}
        />
        <Paper elevation={3} sx={{ width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                <Tab label="Products" {...a11yProps(1)} />
                <Tab label="Transactions" {...a11yProps(2)} />
                <Tab label="Payouts" {...a11yProps(3)} />
              </Tabs>
            </Box>
            <TabPanel value={tabValue} index={0}>
              <Products />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <Transactions />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <Payouts />
            </TabPanel>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AccountProfile;
